// Product data for the Indian portfolio — final brand-tonality copy.
// All headline / short-copy / benefits / FAQs written to match musclepharm.com voice
// while keeping the Indian product-specific facts (₹ pricing, 24g protein, 4 ingredients).
//
// NOTE ON INGREDIENTS: exact ingredient lists and per-serving nutritional values
// on the physical pack are the source of truth per FSSAI. This site provides
// brand-level positioning and directs users to the pack label for regulatory data.

export const products = {
  "combat-whey": {
    slug: "combat-whey",
    name: "Combat 100% Whey",
    category: "Whey Protein",
    price: 7849,
    priceLabel: "\u20B97,849",
    mrp: 12499,
    mrpLabel: "\u20B912,499",
    discountPercent: 37,
    priceNote: "MRP inclusive of all taxes",
    shopUrl: "https://nutriride.com/collections/musclepharm/products/musclepharm-combat-whey-protein",
    kicker: "24g Protein",
    headline: "Built for Everyday Performance",
    subhead: "Premium Whey. Simple Nutrition. Quality You Can Trust.",
    intro: "One Formula. Three Great Flavours.",
    shortDescription:
      "Combat 100% Whey is built for athletes who show up, day after day. A premium whey formula engineered to support recovery, lean muscle and consistent training — every scoop delivers 24 grams of protein with a clean, straightforward profile that mixes easy and tastes great. Sourced to meet international quality standards, brought to India by MusclePro Nutrition.",
    flavours: [
      { id: "chocolate-milk",    name: "Chocolate Milk" },
      { id: "smooth-cappuccino", name: "Smooth Cappuccino" },
      { id: "strawberry-cream",  name: "Strawberry Cream" },
    ],
    benefits: [
      {
        label: "24G Protein",
        note: "Every scoop delivers 24 grams of high-quality whey protein to support recovery and daily performance.",
      },
      {
        label: "Everyday Performance",
        note: "Formulated for the athletes who train consistently — every session, every day.",
      },
      {
        label: "Premium Whey",
        note: "Whey protein sourced to meet international quality standards. Every batch built on trust.",
      },
      {
        label: "Simple Nutrition",
        note: "A clean, focused formula. No unnecessary fillers — just what an athlete actually needs.",
      },
    ],
    ingredients:
      "Combat 100% Whey is built on premium whey protein, sourced and manufactured to international quality standards. Every batch is designed to deliver a clean protein profile that supports serious training — with a focus on mixability, taste and consistency. Refer to the label on your pack for the complete ingredient statement, nutritional information and allergen details, published in accordance with FSSAI guidelines for the Indian market.",
    suggestedUse:
      "Mix 1 scoop of Combat 100% Whey with 250–300 ml of cold water or milk. Shake or stir well and consume immediately after training, first thing in the morning, or any time you need a premium protein hit. For best results, take Combat 100% Whey daily as part of a balanced diet and a consistent training plan. Suitable for adult athletes. Refer to pack label for the manufacturer's recommended usage.",
    faqs: [
      {
        q: "How much protein does Combat 100% Whey deliver per serving?",
        a: "Every scoop of Combat 100% Whey delivers 24 grams of high-quality protein, sourced to meet international standards. Refer to the pack label for the full nutritional information.",
      },
      {
        q: "Which flavours is Combat 100% Whey available in?",
        a: "Combat 100% Whey is available in three great flavours — Chocolate Milk, Smooth Cappuccino, and Strawberry Cream. One formula. Three great flavours.",
      },
      {
        q: "When is the best time to take Combat 100% Whey?",
        a: "Combat 100% Whey is designed to work any time you need premium protein — post-training for recovery, as a morning protein boost, or between meals to help you hit your daily protein target.",
      },
      {
        q: "Where can I buy Combat 100% Whey in India?",
        a: "Combat 100% Whey will be available on Amazon India through our authorised sellers — Nutriride and ADS Gallery. Every genuine unit ships with a TrueMPN authenticity sticker.",
      },
      {
        q: "How do I verify the authenticity of my unit?",
        a: "Every genuine unit carries a TrueMPN authenticity sticker. Scan or enter the code on the TrueMPN app in seconds — visit the Authenticity page for the direct link.",
      },
    ],
  },

  "combat-preworkout": {
    slug: "combat-preworkout",
    name: "Combat Preworkout",
    category: "Preworkout",
    price: 1999,
    priceLabel: "\u20B91,999",
    mrp: 3999,
    mrpLabel: "\u20B93,999",
    discountPercent: 50,
    priceNote: "MRP inclusive of all taxes",
    shopUrl: "https://nutriride.com/collections/musclepharm/products/musclepharm-combat-pre-workout",
    kicker: "Four Ingredients. Made to Perform.",
    headline: "Start Strong. Finish Strong.",
    subhead: "Ready When You Are.",
    intro: "One Formula. Three Flavours.",
    shortDescription:
      "Combat Preworkout is a focused, four-ingredient formula built to help athletes start strong and finish strong. Engineered for real training — not marketing — with clean pre-training energy and a sharp, focused push that shows up in every rep. Brought to India by MusclePro Nutrition.",
    flavours: [
      { id: "watermelon",    name: "Watermelon" },
      { id: "fruit-punch",   name: "Fruit Punch" },
      { id: "melon-hwachae", name: "Melon Hwachae" },
    ],
    benefits: [
      {
        label: "Start Strong",
        note: "A focused push before the first set — clean pre-training energy that shows up when you show up.",
      },
      {
        label: "Finish Strong",
        note: "Engineered to support sustained intensity through the toughest part of every session.",
      },
      {
        label: "Four Ingredients",
        note: "A tight, purpose-selected formula. Nothing hidden. Nothing extra. Just what works.",
      },
      {
        label: "Ready When You Are",
        note: "Mixes fast, hits fast. No long ramp-up between the shaker and the first rep.",
      },
    ],
    ingredients:
      "Combat Preworkout is built around four purpose-selected ingredients, designed to deliver a clean, focused push before every session — without unnecessary fillers. Every batch is manufactured to international quality standards. Refer to the label on your pack for the complete ingredient statement, nutritional information and allergen details, published in accordance with FSSAI guidelines for the Indian market.",
    suggestedUse:
      "Mix 1 scoop of Combat Preworkout with 200–250 ml of cold water and consume 20–30 minutes before your training session. If you are new to preworkout supplements, start with half a scoop to assess tolerance. Do not exceed 1 scoop in a 24-hour period. Not recommended within 6 hours of bedtime. For adult athletes only. Refer to pack label for the manufacturer's recommended usage.",
    faqs: [
      {
        q: "When should I take Combat Preworkout?",
        a: "Combat Preworkout is designed to be taken 20–30 minutes before your training session. That gives the formula time to hit before the first set.",
      },
      {
        q: "Which flavours is Combat Preworkout available in?",
        a: "Combat Preworkout is available in three flavours — Watermelon, Fruit Punch and Melon Hwachae. One formula. Three flavours.",
      },
      {
        q: "How do I dose it if I am new to preworkout?",
        a: "Start with half a scoop mixed with 200 ml of cold water to assess your tolerance. Once comfortable, you can move to a full scoop 20–30 minutes before training.",
      },
      {
        q: "Can I take Combat Preworkout every day?",
        a: "Combat Preworkout is designed for training days. We recommend using it before your harder sessions and cycling off periodically to keep sensitivity high. For adult athletes only.",
      },
      {
        q: "Where can I buy Combat Preworkout in India?",
        a: "Combat Preworkout will be available on Amazon India through our authorised sellers — Nutriride and ADS Gallery. Every genuine unit ships with a TrueMPN authenticity sticker.",
      },
      {
        q: "How do I verify the authenticity of my unit?",
        a: "Every genuine unit carries a TrueMPN authenticity sticker. Scan or enter the code on the TrueMPN app in seconds — visit the Authenticity page for the direct link.",
      },
    ],
  },
};

export const productList = [products["combat-whey"], products["combat-preworkout"]];
